import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { ChevronRight, Calendar, User } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import PublicHeader from '../components/PublicHeader';
import { PublicFooter } from '../components/SEOLandingTemplate';
import FloatingWhatsapp from '../components/FloatingWhatsapp';
import RequestQuoteDialog from '../components/RequestQuoteDialog';
import { COMPANY, organizationJsonLd } from '../content/seo';
import { trackEvent } from '../utils/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  // 75% scroll tracker for blog engagement
  useEffect(() => {
    if (!post) return;
    let fired = false;
    const onScroll = () => {
      if (fired) return;
      const scrolled = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight;
      if (scrolled >= 0.75) {
        fired = true;
        trackEvent('blog_read_75pct', { slug, title: post.title });
        window.removeEventListener('scroll', onScroll);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [post, slug]);

  if (notFound) return <Navigate to="/blog" replace />;
  if (!post) return null;

  const path = `/blog/${slug}`;
  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    image: post.cover_image_url ? [post.cover_image_url] : undefined,
    author: { '@type': 'Person', name: post.author_name },
    publisher: {
      '@type': 'Organization',
      name: COMPANY.name,
      logo: { '@type': 'ImageObject', url: `${COMPANY.site}/logo.png` },
    },
    datePublished: post.published_at,
    dateModified: post.updated_at,
    mainEntityOfPage: `${COMPANY.site}${path}`,
  };
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: COMPANY.site },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${COMPANY.site}/blog` },
      { '@type': 'ListItem', position: 3, name: post.title, item: `${COMPANY.site}${path}` },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={post.title}
        description={post.excerpt || post.title}
        path={path}
        image={post.cover_image_url}
        jsonLd={[articleJsonLd, breadcrumbJsonLd, organizationJsonLd()]}
      />
      <PublicHeader onQuote={() => setQuoteOpen(true)} />

      <nav aria-label="Breadcrumb" className="max-w-3xl mx-auto px-4 pt-6 text-sm text-neutral-500">
        <ol className="flex items-center gap-2 flex-wrap">
          <li><Link to="/" className="hover:text-emerald-700">Home</Link></li>
          <ChevronRight className="w-3 h-3" />
          <li><Link to="/blog" className="hover:text-emerald-700">Blog</Link></li>
          <ChevronRight className="w-3 h-3" />
          <li aria-current="page" className="text-neutral-900 font-medium truncate">{post.title}</li>
        </ol>
      </nav>

      <article className="max-w-3xl mx-auto px-4 py-10">
        <p className="text-emerald-700 text-sm uppercase font-semibold tracking-wide">{post.category}</p>
        <h1 className="text-4xl sm:text-5xl font-serif font-bold text-neutral-900 mt-2 leading-tight">
          {post.title}
        </h1>
        <div className="mt-3 text-sm text-neutral-500 flex items-center gap-4">
          <span className="inline-flex items-center gap-1"><User className="w-4 h-4" /> {post.author_name}</span>
          {post.published_at && (
            <span className="inline-flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {new Date(post.published_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
          )}
        </div>

        {post.cover_image_url && (
          <img
            src={post.cover_image_url}
            alt={post.title}
            className="mt-6 w-full aspect-video object-cover rounded-xl"
          />
        )}

        <div className="prose prose-neutral prose-lg max-w-none mt-8" data-testid="blog-content">
          <ReactMarkdown>{post.content || ''}</ReactMarkdown>
        </div>

        {post.tags?.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-2">
            {post.tags.map((t) => (
              <Link
                key={t}
                to={`/blog?tag=${encodeURIComponent(t)}`}
                className="px-3 py-1 rounded-full text-xs border border-neutral-200 text-neutral-700 hover:border-emerald-700"
              >
                #{t}
              </Link>
            ))}
          </div>
        )}
      </article>

      {post.related?.length > 0 && (
        <section className="bg-neutral-50 border-t border-neutral-200">
          <div className="max-w-3xl mx-auto px-4 py-12">
            <h2 className="text-2xl font-serif font-bold text-neutral-900">Related articles</h2>
            <div className="grid sm:grid-cols-3 gap-4 mt-6">
              {post.related.map((r) => (
                <Link
                  key={r.id}
                  to={`/blog/${r.slug}`}
                  className="block p-4 rounded-lg border border-neutral-200 bg-white hover:border-emerald-700"
                >
                  <p className="text-xs text-emerald-700 font-semibold uppercase">{r.category}</p>
                  <h3 className="font-serif font-bold mt-1 text-neutral-900">{r.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <PublicFooter />
      <FloatingWhatsapp />
      <RequestQuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
    </div>
  );
}
